"use client"

import { useState, useEffect } from "react"
import { Mail, Phone, Facebook, Linkedin, ChevronDown, CheckCircle } from "lucide-react"
import LandingDemoStudyBuddy from "./landing-demo-studybudy"
import LandingDemoAssignment from "./landing-demo-assignment"

export default function LandingPage() {
  const [openAccordion, setOpenAccordion] = useState(null)
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false)
  const [demoMode, setDemoMode] = useState("study-buddy")

  // Demo Usage Tracking
  const [studyBuddyDemoUsed, setStudyBuddyDemoUsed] = useState(false)
  const [assignmentDemoUsed, setAssignmentDemoUsed] = useState(false)

  // NEW: Track if demo was used at the time of component rendering
  const [studyBuddyDemoUsedAtRender, setStudyBuddyDemoUsedAtRender] = useState(false)
  const [assignmentDemoUsedAtRender, setAssignmentDemoUsedAtRender] = useState(false)

  useEffect(() => {
    // Initialize demo flags from localStorage on component mount
    const sbUsed = localStorage.getItem("studyBuddyDemoUsed")
    if (sbUsed === null) {
      localStorage.setItem("studyBuddyDemoUsed", "false")
      setStudyBuddyDemoUsed(false)
      setStudyBuddyDemoUsedAtRender(false) // NEW: Set render-time state
    } else {
      const isUsed = sbUsed === "true"
      setStudyBuddyDemoUsed(isUsed)
      setStudyBuddyDemoUsedAtRender(isUsed) // NEW: Set render-time state
    }

    const assignUsed = localStorage.getItem("assignmentDemoUsed")
    if (assignUsed === null) {
      localStorage.setItem("assignmentDemoUsed", "false")
      setAssignmentDemoUsed(false)
      setAssignmentDemoUsedAtRender(false) // NEW: Set render-time state
    } else {
      const isUsed = assignUsed === "true"
      setAssignmentDemoUsed(isUsed)
      setAssignmentDemoUsedAtRender(isUsed) // NEW: Set render-time state
    }
  }, [])

  const toggleAccordion = (value) => {
    setOpenAccordion(openAccordion === value ? null : value)
  }

  const showOverallDemoLimit = studyBuddyDemoUsed && assignmentDemoUsed

  const scrollToSection = (id) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
  }

  // NEW: Component to show demo used message for Study Buddy
  const StudyBuddyDemoUsedMessage = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center h-[400px] sm:h-[600px] border border-gray-200 rounded-lg bg-gray-50">
      <h4 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">Study Buddy Demo Used!</h4>
      <p className="text-orange-600 font-semibold mb-4">You've already used your free Study Buddy demo.</p>
      <p className="text-gray-500 text-sm mb-4">Please login/signup to continue using Study Buddy.</p>
      <a href="/login" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors">
        Login / Sign Up
      </a>
    </div>
  )

  // NEW: Component to show demo used message for Assignment
  const AssignmentDemoUsedMessage = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center h-[400px] sm:h-[600px] border border-gray-200 rounded-lg bg-gray-50">
      <h4 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">Assignment Demo Used!</h4>
      <p className="text-orange-600 font-semibold mb-4">You've already used your free Assignment demo.</p>
      <p className="text-gray-500 text-sm mb-4">Please login/signup to continue using Assignment Creator.</p>
      <a href="/login" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors">
        Login / Sign Up
      </a>
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#EBF0F9]">
      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4 sm:px-8 py-6 text-white">
        <div className="flex items-center space-x-4 sm:space-x-12">
          <div className="flex items-center space-x-2">
            <div className="logo-name mt-2">
              <a href="/" className="flex items-center gap-2 py-2">
                <div>
                  <h1 className="flex text-xl sm:text-2xl md:text-3xl font-semibold text-white">
                    <span>पढ़.</span>
                    <span className="gradient-text font-semibold">AI</span>
                  </h1>
                </div>
              </a>
            </div>
          </div>
          <div className="hidden md:flex space-x-8">
            <a
              href="#demo-section"
              onClick={(e) => {
                e.preventDefault()
                scrollToSection("demo-section")
              }}
              className="hover:text-gray-300 text-white text-sm lg:text-base"
            >
              Try Demo
            </a>
            <a
              href="#features-section"
              onClick={(e) => {
                e.preventDefault()
                scrollToSection("features-section")
              }}
              className="hover:text-gray-300 text-white text-sm lg:text-base"
            >
              Features
            </a>
          </div>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4">
          <a
            href="/login"
            className="text-white hover:text-gray-300 px-2 sm:px-4 py-2 rounded-full transition-colors text-sm lg:text-base"
          >
            Sign In
          </a>
          <a
            href="/signup"
            className="bg-red-500 hover:bg-red-600 text-white px-3 sm:px-6 py-2 rounded-full transition-colors text-sm lg:text-base"
          >
            Sign up
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white min-h-screen">
        {/* Background gradient image */}
        <div className="absolute inset-0 -top-48">
          <img
            src="/images/gradient-background.png"
            alt="Gradient background"
            className="object-cover w-full h-[130vh] absolute inset-0"
            width={1920}
            height={1080}
          />
        </div>
        <div className="relative z-10">
          <div className="container mx-auto px-4 sm:px-8 py-20 pt-28">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center min-h-[600px]">
              {/* Left side - Text content */}
              <div className="space-y-6 text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                  From Chalkboards
                  <br />
                  to Chatbots
                </h1>
                <p className="text-base sm:text-lg text-white/90 max-w-lg leading-relaxed mx-auto lg:mx-0">
                  Padhai by Aura brings you a suite of AI tools created for educators. Create Assignments, Quizzes,
                  Courses or live classes all in one easy platform so you can spend more time teaching.
                </p>
                <a
                  href="/bookADemo"
                  className="bg-gray-900 hover:bg-gray-800 text-white px-6 sm:px-8 py-3 text-base sm:text-lg rounded-full transition-colors inline-flex items-center justify-center"
                >
                  Book Your Demo →
                </a>
              </div>
              {/* Right side - Empty space for absolute positioned image */}
              <div className="relative hidden lg:block"></div>
            </div>
          </div>
          {/* Hero students image - positioned absolutely to not affect text layout */}
          <div className="absolute top-0 right-0 hidden lg:block" style={{ top: "-150px", right: "-200px" }}>
            <img
              src="/images/hero-students.png"
              alt="Students reading on stacked books with educational elements"
              className="relative z-10"
              style={{
                filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.1))",
                width: "1200px",
                height: "1267px",
                objectFit: "contain",
              }}
              width={1200}
              height={1267}
            />
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-40 right-24 w-20 h-20 bg-red-400 rounded-lg rotate-45 opacity-90 z-20 hidden lg:block"></div>
      </section>

      {/* Key Features Section */}
      <section id="features-section" className="py-12 sm:py-20 bg-gray-50 relative">
        {/* Background decorative vectors */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg className="absolute top-20 left-10 w-32 h-32 text-gray-200" viewBox="0 0 100 100" fill="none">
            <path d="M10 10 L90 90 M90 10 L10 90" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
          </svg>
          <svg className="absolute bottom-20 right-10 w-40 h-40 text-gray-200" viewBox="0 0 100 100" fill="none">
            <path d="M20 20 L80 50 L20 80" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
          </svg>
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          {/* Generate Question Feature - Content Left, Image Right */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-16">
            <div className="space-y-6 order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl font-bold text-blue-600">Generate Question</h2>
              <p
                className="text-gray-600 leading-[150%] text-base sm:text-lg lg:text-[20px]"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, letterSpacing: "0%" }}
              >
                Create assignments, quizzes, and essays in minutes using any text, topic, or material. No more
                late-night lesson planning or manual question setting. With AI-powered generation, get high-quality
                academic content instantly. Customize difficulty, formats, and topics as per your needs.
              </p>
            </div>
            <div className="relative order-1 lg:order-2">
              <img
                src="/images/ai-assistant.png"
                alt="AI Assistant - Woman using tablet with AI elements"
                className="w-full h-auto rounded-2xl"
                width={600}
                height={400}
              />
            </div>
          </div>
          {/* Study Buddy Chat Feature - Chat Left, Content Right */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-20 rounded-3xl p-4 sm:p-8 relative overflow-hidden">
            {/* Left side - Chat Interface */}
            <div className="relative z-10 flex flex-col items-end space-y-4 sm:space-y-6 p-4 sm:p-6 w-full min-h-[400px] justify-between order-2 lg:order-1">
              {/* Message 1: Study Buddy */}
              <div className="flex items-start space-x-4 w-full">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <img
                    src="/images/study-buddy-avatar.png"
                    alt="Study Buddy Avatar"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                </div>
                <div className="bg-gray-100 p-3 rounded-xl rounded-tl-none max-w-[75%]">
                  <p className="text-sm sm:text-lg text-gray-800">Hi Vipul. How can I help you?</p>
                  <p className="text-xs sm:text-base text-gray-500 mt-1">Study Buddy, 12:35</p>
                </div>
              </div>
              {/* Message 2: Vipul */}
              <div className="flex items-end justify-end space-x-4 w-full">
                <div className="bg-blue-600 text-white p-3 rounded-xl rounded-br-none max-w-[75%]">
                  <p className="text-sm sm:text-lg">Tell me about black holes.</p>
                  <p className="text-xs sm:text-base text-blue-200 mt-1 text-right">Vipul, 12:36</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <img
                    src="/images/vipul-avatar.png"
                    alt="User Avatar"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                </div>
              </div>
              {/* Message 3: Study Buddy */}
              <div className="flex items-start space-x-4 w-full">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <img
                    src="/images/study-buddy-avatar.png"
                    alt="Study Buddy Avatar"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                </div>
                <div className="bg-gray-100 p-3 rounded-xl rounded-tl-none max-w-[75%]">
                  <p className="text-sm sm:text-lg text-gray-800">
                    Super dense regions where gravity traps everything, even light. Not vacuums!
                  </p>
                  <p className="text-xs sm:text-base text-gray-500 mt-1">Study Buddy, 12:36</p>
                </div>
              </div>
              {/* Message 4: Vipul */}
              <div className="flex items-end justify-end space-x-4 w-full">
                <div className="bg-blue-600 text-white p-3 rounded-xl rounded-br-none max-w-[75%]">
                  <p className="text-sm sm:text-lg">What is the significance of the event horizon?</p>
                  <p className="text-xs sm:text-base text-blue-200 mt-1 text-right">Vipul, 12:37</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <img
                    src="/images/vipul-avatar.png"
                    alt="User Avatar"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                </div>
              </div>
              {/* Message 5: Study Buddy */}
              <div className="flex items-start space-x-4 w-full">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <img
                    src="/images/study-buddy-avatar.png"
                    alt="Study Buddy Avatar"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                </div>
                <div className="bg-gray-100 p-3 rounded-xl rounded-tl-none max-w-[75%]">
                  <p className="text-sm sm:text-lg text-gray-800">
                    It's the point of no return! Once something crosses it, it can't escape the black hole's gravity.
                  </p>
                  <p className="text-xs sm:text-base text-gray-500 mt-1">Study Buddy, 12:38</p>
                </div>
              </div>
            </div>
            {/* Right side - Content */}
            <div className="space-y-6 relative z-10 order-1 lg:order-2">
              <h3 className="text-3xl sm:text-4xl font-bold text-blue-600">Study Buddy Chat</h3>
              <p
                className="text-gray-600 leading-[150%] text-base sm:text-lg lg:text-[20px]"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, letterSpacing: "0%" }}
              >
                An AI-powered assistant that helps you instantly create engaging teaching materials like mind maps,
                flowcharts, flashcards, and more. Simply enter a topic, and the bot transforms it into visual and
                interactive learning aids.
              </p>
              <button
                onClick={() => scrollToSection("demo-section")}
                className="bg-gray-900 hover:bg-gray-800 text-white px-6 sm:px-8 py-3 rounded-full transition-colors flex items-center space-x-2"
              >
                <span>Try Study Buddy</span>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                >
                  <path
                    d="M12 4L20 12L12 20M4 12H20H4Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          {/* E-Classroom Feature - Content Left, Images Right */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-20">
            <div className="space-y-6 order-2 lg:order-1">
              <h3 className="text-3xl sm:text-4xl font-bold text-blue-600">E-Classroom</h3>
              <p
                className="text-gray-600 leading-[150%] text-base sm:text-lg lg:text-[20px]"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, letterSpacing: "0%" }}
              >
                Host live lectures and group discussions seamlessly—whether you're in the classroom or learning from
                home. Engage students in real-time with interactive sessions and collaborative tools. Get automatically
                generated detailed notes, concise summaries, and key takeaways after each session.
              </p>
              <button
                onClick={() => window.open("https://pathshala.aurait.ai/", "_blank")}
                className="bg-gray-900 hover:bg-gray-800 text-white px-6 sm:px-8 py-3 rounded-full transition-colors"
              >
                Learn More →
              </button>
            </div>
            {/* Right side - Image grid */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6 order-1 lg:order-2">
              {/* Purple book image */}
              <div>
                <img
                  src="/images/purple-book.jpg"
                  alt="Open book with colorful pages"
                  className="w-full h-auto rounded-2xl"
                  width={300}
                  height={200}
                />
              </div>
              {/* Teal e-learning tablet image */}
              <div>
                <img
                  src="/images/elearning-tablet.jpg"
                  alt="E-learning tablet interface"
                  className="w-full h-auto rounded-2xl"
                  width={300}
                  height={200}
                />
              </div>
            </div>
          </div>
          {/* Analytics Dashboard Feature - Image Left, Content Right */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-20">
            <div className="relative order-2 lg:order-1">
              <img
                src="/images/analytics-chart.png"
                alt="Weekly analytics chart"
                className="w-full h-auto rounded-2xl"
                width={600}
                height={400}
              />
            </div>
            <div className="space-y-6 order-1 lg:order-2">
              <h3 className="text-3xl sm:text-4xl font-bold text-blue-600">Analytics Dashboard</h3>
              <p
                className="text-gray-600 leading-[150%] text-base sm:text-lg lg:text-[20px]"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, letterSpacing: "0%" }}
              >
                Monitor every student's progress in real-time with a clear, intuitive dashboard. Track performance
                across assignments, quizzes, and participation—all in one place. Spot trends, identify learning gaps,
                and provide timely support with ease.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials & Proof Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-8 sm:mb-12">Testimonials & Proof</h2>
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 max-w-4xl mx-auto mb-8 sm:mb-12">
            <div className="bg-gray-50 p-6 sm:p-8 rounded-lg shadow-md text-left">
              <p className="text-base sm:text-lg italic text-gray-700 mb-4">
                "Padhai cut my grading workload in half. Now I spend more time teaching and less on paperwork."
              </p>
              <p className="font-semibold text-gray-900">— Priya Sharma, High School English Teacher</p>
            </div>
            <div className="bg-gray-50 p-6 sm:p-8 rounded-lg shadow-md text-left">
              <p className="text-base sm:text-lg italic text-gray-700 mb-4">
                "Our students love Study Buddy. It feels like they have a tutor whenever they need one."
              </p>
              <p className="font-semibold text-gray-900">— Rahul Mehta, Middle School Science Instructor</p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo-section" className="py-12 sm:py-20 bg-gray-50 relative">
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center space-y-6 sm:space-y-8 mb-12 sm:mb-20">
            <div className="space-y-4">
              <h3 className="text-3xl sm:text-4xl font-bold">
                <span className="text-blue-600">Experience it now — </span>
                <span className="text-red-500">powerful tools made simple.</span>
              </h3>
              <p className="text-gray-600 text-base sm:text-lg px-4">
                Try out StudyBuddy and Assignment Creator — your AI-powered teaching assistants. Create, plan, and
                manage lessons effortlessly in minutes.
              </p>
            </div>
            {/* Toggle for Study Buddy / Assignment */}
            <div className="flex justify-center gap-4 mb-8">
              <button
                onClick={() => setDemoMode("study-buddy")}
                className={`px-4 sm:px-6 py-2 rounded-full text-base sm:text-lg font-semibold transition-colors ${
                  demoMode === "study-buddy"
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Study Buddy {studyBuddyDemoUsed && <CheckCircle className="inline-block w-4 h-4 ml-1" />}
              </button>
              <button
                onClick={() => setDemoMode("assignment")}
                className={`px-4 sm:px-6 py-2 rounded-full text-base sm:text-lg font-semibold transition-colors ${
                  demoMode === "assignment"
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Assignment {assignmentDemoUsed && <CheckCircle className="inline-block w-4 h-4 ml-1" />}
              </button>
            </div>
            {/* Render the appropriate demo component */}
            <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg p-2 sm:p-4">
              {showOverallDemoLimit ? (
                <div className="flex flex-col items-center justify-center py-12 text-center h-[400px] sm:h-[600px] border border-gray-200 rounded-lg bg-gray-50">
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">Demo Limit Reached!</h4>
                  <p className="text-red-500 font-semibold mb-4">
                    You've used your free demo for both Study Buddy and Assignment.
                  </p>
                  <p className="text-gray-500 text-sm mb-4">Please login/signup to continue using Padhai.</p>
                  <a
                    href="/login"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors"
                  >
                    Login / Sign Up
                  </a>
                </div>
              ) : demoMode === "study-buddy" ? (
                // NEW: Show demo used message only if it was used at render time
                studyBuddyDemoUsedAtRender ? (
                  <StudyBuddyDemoUsedMessage />
                ) : (
                  <LandingDemoStudyBuddy
                    studyBuddyDemoUsed={studyBuddyDemoUsed}
                    setStudyBuddyDemoUsed={setStudyBuddyDemoUsed}
                  />
                )
              ) : // NEW: Show demo used message only if it was used at render time
              assignmentDemoUsedAtRender ? (
                <AssignmentDemoUsedMessage />
              ) : (
                <LandingDemoAssignment
                  assignmentDemoUsed={assignmentDemoUsed}
                  setAssignmentDemoUsed={setAssignmentDemoUsed}
                />
              )}
            </div>
          </div>
          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-8 sm:mb-12">
              <h3 className="text-3xl sm:text-4xl font-bold">
                <span className="text-blue-600">Frequently </span>
                <span className="text-red-500">asked </span>
                <span className="text-blue-600">questions</span>
              </h3>
              <p
                className="text-[#6A6D9E] leading-[150%] text-lg sm:text-[24px] text-center max-w-2xl mx-auto px-4"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, letterSpacing: "0%" }}
              >
                If you are not sure whether Padhai is suitable for you or not, do not worry. We are here to explain
                everything you might want to know.
              </p>
            </div>
            {/* FAQ Container with blue border */}
            <div className="border-2 border-blue-400 rounded-lg bg-white">
              <div>
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left leading-[150%] text-lg sm:text-[24px] hover:bg-gray-50 flex justify-between items-center transition-colors"
                    style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, letterSpacing: "0%", color: "#16194F" }}
                    onClick={() => toggleAccordion("item-1")}
                  >
                    What is Padhai?
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${openAccordion === "item-1" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openAccordion === "item-1" && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600">
                      Padhai is an online platform that helps teachers make assignments and quizzes in minutes and lets
                      students practice and get feedback right away. It also shows teachers how each student is doing so
                      they can give extra help where it's needed.
                    </div>
                  )}
                </div>
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left leading-[150%] text-lg sm:text-[24px] hover:bg-gray-50 flex justify-between items-center transition-colors"
                    style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, letterSpacing: "0%", color: "#16194F" }}
                    onClick={() => toggleAccordion("item-2")}
                  >
                    What subjects can Padhai help me with?
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${openAccordion === "item-2" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openAccordion === "item-2" && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600">
                      Right now we cover school-level math, physics, chemistry, biology and English. We'll soon add
                      social studies, computer science and special modules for exams like NEET and JEE.
                    </div>
                  )}
                </div>
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left leading-[150%] text-lg sm:text-[24px] hover:bg-gray-50 flex justify-between items-center transition-colors"
                    style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, letterSpacing: "0%", color: "#16194F" }}
                    onClick={() => toggleAccordion("item-3")}
                  >
                    Is Padhai suitable for all age groups?
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${openAccordion === "item-3" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openAccordion === "item-3" && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600">
                      Yes. You pick the grade or topic you want, and Padhai will match the question difficulty to each
                      student. It works just as well for younger learners as for older ones.
                    </div>
                  )}
                </div>
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left leading-[150%] text-lg sm:text-[24px] hover:bg-gray-50 flex justify-between items-center transition-colors"
                    style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, letterSpacing: "0%", color: "#16194F" }}
                    onClick={() => toggleAccordion("item-4")}
                  >
                    Can Padhai help me prepare for exams?
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${openAccordion === "item-4" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openAccordion === "item-4" && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600">
                      Definitely. You can take full mock tests that look like the real thing, drill on topics you find
                      hard and see clear reports on where you need more practice.
                    </div>
                  )}
                </div>
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left leading-[150%] text-lg sm:text-[24px] hover:bg-gray-50 flex justify-between items-center transition-colors"
                    style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, letterSpacing: "0%", color: "#16194F" }}
                    onClick={() => toggleAccordion("item-5")}
                  >
                    How do i get started with Padhai?
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${openAccordion === "item-5" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openAccordion === "item-5" && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600">
                      1. Go to our website and sign up for the free pilot as a student or a teacher. 2. Create your
                      class and pick your subjects and grades. 3. Share the class code or link with your students. 4.
                      Create AI generated Assignments and Quiz 5. Watch their progress online and add more work as you
                      go. If you need any help, our support team and guides are just a click away.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-100 py-8 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Left side - Logo and Social */}
            <div className="space-y-4 sm:space-y-6 col-span-1 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-gray-900 rounded-sm flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-sm"></div>
                </div>
                <div className="text-xl font-bold text-gray-900">Padhai</div>
              </div>
              <div className="flex space-x-4">
                <a
                  href="https://m.facebook.com/profile.php?id=61577298545832&name=xhp_nt__fb__action__open_user"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5 text-gray-500 hover:text-blue-600 cursor-pointer transition-colors" />
                </a>
                <a
                  href="https://www.linkedin.com/company/padhai-by-aura/posts/?feedView=all"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5 text-gray-500 hover:text-blue-600 cursor-pointer transition-colors" />
                </a>
              </div>
              <div className="text-sm text-gray-600">
                <p>Copyright © 2025 Padhai LLC.</p>
                <p>All rights reserved.</p>
              </div>
            </div>
            {/* Navigation Links - Column 1 */}
            <div className="space-y-4">
              <a href="#features-section" className="block text-gray-700 hover:text-blue-600 transition-colors">
                Features
              </a>
              <a href="/bookADemo" className="block text-gray-700 hover:text-blue-600 transition-colors">
                Book a Demo
              </a>
              <button
                onClick={() => setShowPrivacyPolicy(true)}
                className="block text-gray-700 hover:text-blue-600 transition-colors text-left"
              >
                Privacy Policy
              </button>
            </div>
            {/* Navigation Links - Column 2 */}
            <div className="space-y-4">
              <a href="#" className="block text-gray-700 hover:text-blue-600 transition-colors">
                About
              </a>
              <a href="#" className="block text-gray-700 hover:text-blue-600 transition-colors">
                Blog
              </a>
            </div>
            {/* Contact Information */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2 text-gray-600">
                <Mail className="w-4 h-4" />
                <span className="text-sm sm:text-base">support@aurait.ai</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <Phone className="w-4 h-4" />
                <span className="text-sm sm:text-base">+91 9625697024</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto w-full mx-4">
            <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Privacy Policy</h2>
              <button
                onClick={() => setShowPrivacyPolicy(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="px-4 sm:px-6 py-6 space-y-6 text-gray-700">
              <p className="text-sm text-gray-500">Effective Date: 16 July, 2025</p>
              <p>
                Aura-AI Tech Solutions PVT. Ltd. ("Padhai," "we," "us," or "our") values your privacy. This Privacy
                Policy explains how we collect, use, disclose, and protect your personal information when you visit and
                use the Padhai website and its associated services (collectively, the "Services").
              </p>
              <p>
                We encourage you to read this policy carefully to understand how we handle your data and what rights you
                have.
              </p>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Updates to This Privacy Policy</h3>
                <p className="mb-3">
                  We may modify this Privacy Policy from time to time to reflect changes in legal requirements, business
                  practices, or service enhancements. If we make material changes, we will:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Update the "Effective Date" at the top of this policy.</li>
                  <li>Notify you through email or in-app notifications (where applicable).</li>
                </ul>
                <p className="mt-3">
                  Your continued use of the Services after changes are posted constitutes acceptance of the revised
                  Privacy Policy.
                </p>
              </div>
              {/* Rest of privacy policy content... */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">10. Contact Us</h3>
                <p className="mb-2">For privacy-related questions or to exercise your data rights, contact us:</p>
                <p>Aura-AI Tech Solutions PVT. Ltd.</p>
                <p>📧 Email: support@padhai.ai</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
